import { BULK_DELETE_MAX_IDS } from '@bower/shared/constants/bulk';
import { apiService } from '../services/api.service';
import { bulkDeleteArtifacts } from '../services/api/artifacts.api';
import { bulkDeleteProjects } from '../services/api/projects.api';

/**
 * Result from a bulk delete operation
 */
export interface BulkDeleteResult {
  succeeded: string[];
  failed: Array<{ id: string; error: string }>;
  total: number;
}

// The bulk-delete routes cap batches as an abuse guard (projects/artifacts
// controllers). A legitimate select-all can exceed the cap — selection is
// unbounded under infinite scroll, and ProjectDetail loads all children in
// one fetch — so the client chunks and the server cap should only ever fire
// on crafted requests (PR #501 review). Chunk size and both server .max()s
// derive from the same shared constant, so they cannot drift (PR #507
// review: the previous keep-in-sync comments left backend tightening
// shipping green).
const BULK_DELETE_CHUNK_SIZE = BULK_DELETE_MAX_IDS;

type BulkDeleteEntityType = 'projects' | 'artifacts';

function callBulkDeleteApi(entityType: BulkDeleteEntityType, ids: string[]) {
  return entityType === 'projects'
    ? bulkDeleteProjects(apiService, ids)
    : bulkDeleteArtifacts(apiService, ids);
}

/**
 * Splits a failed bulk-delete envelope into per-id tallies. A partial failure
 * (PARTIAL_DELETE_FAILURE) carries the true failed list in `error.details` —
 * ids not listed there actually deleted and must count as succeeded, or the
 * UI keeps ghost rows for them. Fail-closed is per-envelope: if ANY detail
 * row can't be matched to a requested id (no string `id`, or an id we never
 * requested), the whole envelope is untrustworthy — that row may be the
 * server reporting a failure we'd otherwise promote to succeeded — so every
 * requested id counts as failed, never "all succeeded" (PR #492 review).
 */
function tallyFailedEnvelope(
  error: { message?: string; details?: unknown } | undefined,
  requestedIds: string[],
  fallbackMessage: string,
): Pick<BulkDeleteResult, 'succeeded' | 'failed'> {
  const raw = Array.isArray(error?.details)
    ? (error.details as Array<{ id?: unknown; error?: unknown }>)
    : null;
  const details = (raw ?? []).filter(
    (d): d is { id: string; error?: unknown } =>
      typeof d?.id === 'string' && requestedIds.includes(d.id),
  );
  if (raw && details.length > 0 && details.length === raw.length) {
    const failedIds = new Set(details.map((d) => d.id));
    return {
      failed: details.map((d) => ({
        id: d.id,
        error: typeof d.error === 'string' && d.error ? d.error : fallbackMessage,
      })),
      succeeded: requestedIds.filter((id) => !failedIds.has(id)),
    };
  }
  const message = error?.message || fallbackMessage;
  return { failed: requestedIds.map((id) => ({ id, error: message })), succeeded: [] };
}

/**
 * Deletes one entity type in <=500-id chunks, aggregating per-id tallies.
 * Handling per chunk is structural (any failed envelope -> per-id tallies via
 * tallyFailedEnvelope): a later chunk's failure must never discard an earlier
 * chunk's completed deletions (rule 12), so this never throws.
 */
async function deleteTypeInChunks(
  entityType: BulkDeleteEntityType,
  ids: string[],
): Promise<Pick<BulkDeleteResult, 'succeeded' | 'failed'>> {
  const tally: Pick<BulkDeleteResult, 'succeeded' | 'failed'> = { succeeded: [], failed: [] };
  for (let i = 0; i < ids.length; i += BULK_DELETE_CHUNK_SIZE) {
    const chunk = ids.slice(i, i + BULK_DELETE_CHUNK_SIZE);
    const response = await callBulkDeleteApi(entityType, chunk);
    const data = response.data as BulkDeleteResult | undefined;
    // Array.isArray guards uphold "never throws": a malformed data-bearing
    // 2xx (data: {}) would make push(...undefined) throw mid-loop and
    // discard earlier chunks' tallies — route it to the fail-closed
    // reconstruction instead (PR #507 review).
    if (response.success && Array.isArray(data?.succeeded) && Array.isArray(data?.failed)) {
      tally.succeeded.push(...data.succeeded);
      tally.failed.push(...data.failed);
    } else {
      const chunkTally = tallyFailedEnvelope(
        response.error,
        chunk,
        `Failed to delete ${entityType}`,
      );
      tally.succeeded.push(...chunkTally.succeeded);
      tally.failed.push(...chunkTally.failed);
    }
  }
  return tally;
}

/**
 * Performs bulk delete for a single entity type
 * Note: Entity deletions are broadcast via WebSocket by the backend
 */
export async function bulkDelete(
  entityType: BulkDeleteEntityType,
  ids: string[],
  _workspaceId?: string, // Kept for backwards compatibility
): Promise<BulkDeleteResult> {
  // Dedupe: one call site (ProjectDetail's row-click append) can produce
  // duplicate selections, which double-count tallies and render incoherent
  // "Moved 0 of 2… 1 failed" counts (PR #500 review).
  const uniqueIds = [...new Set(ids)];

  if (uniqueIds.length <= BULK_DELETE_CHUNK_SIZE) {
    // Single chunk keeps the established contract: full success returns the
    // server payload; a 409 PARTIAL_DELETE_FAILURE means some ids DID delete,
    // so return the per-id tallies (callers render the partial-failure toast)
    // instead of discarding the partial result; a total failure throws.
    const response = await callBulkDeleteApi(entityType, uniqueIds);

    if (response.success && response.data) {
      return response.data as BulkDeleteResult;
    }

    if (response.error?.code === 'PARTIAL_DELETE_FAILURE') {
      return {
        ...tallyFailedEnvelope(response.error, uniqueIds, `Failed to delete ${entityType}`),
        total: uniqueIds.length,
      };
    }

    throw new Error(response.error?.message || `Failed to delete ${entityType}`);
  }

  // Multi-chunk (>500 ids — a large select-all): never throw, or a later
  // chunk's failure would report ids an earlier chunk already deleted as
  // undeleted. Failures surface as per-id tallies and callers render the
  // partial-failure toast with true counts.
  return {
    ...(await deleteTypeInChunks(entityType, uniqueIds)),
    total: uniqueIds.length,
  };
}

/**
 * Performs bulk delete for mixed entity types (used in ProjectDetail)
 * Note: Entity deletions are broadcast via WebSocket by the backend
 */
export async function bulkDeleteMixed(
  itemsByType: {
    projects?: string[];
    artifacts?: string[];
  },
  _workspaceId?: string, // Kept for backwards compatibility
  _parentId?: string, // Kept for backwards compatibility
): Promise<BulkDeleteResult> {
  const results: BulkDeleteResult = {
    succeeded: [],
    failed: [],
    total: 0,
  };

  for (const entityType of ['projects', 'artifacts'] as const) {
    const ids = [...new Set(itemsByType[entityType] || [])];
    if (ids.length === 0) continue;
    const tally = await deleteTypeInChunks(entityType, ids);
    results.succeeded.push(...tally.succeeded);
    results.failed.push(...tally.failed);
    results.total += ids.length;
  }

  return results;
}
